"use client";

import {
  Camera,
  Car,
  CheckCircle2,
  Loader2,
  X,
} from "lucide-react";
import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ApiError } from "@/lib/api";
import { createPublicLead } from "@/lib/leads";
import { lookupPostalCode } from "@/lib/postal-code";
import type { PublicProduct } from "@/types/store";

export type VehicleLeadMode =
  | "financing"
  | "vehicle_appraisal";

type VehicleLeadModalProps = {
  companySlug: string;
  mode: VehicleLeadMode;
  products: PublicProduct[];
  onClose: () => void;
};

const inputClass =
  "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500";

function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

function formatCep(value: string) {
  const digits = onlyDigits(value).slice(0, 8);

  return digits.replace(/^(\d{5})(\d)/, "$1-$2");
}

function formatCpf(value: string) {
  const digits = onlyDigits(value).slice(0, 11);

  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(
      /^(\d{3})\.(\d{3})\.(\d{3})(\d)/,
      "$1.$2.$3-$4",
    );
}

function formatPlate(value: string) {
  return value
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
    .slice(0, 7);
}

export default function VehicleLeadModal({
  companySlug,
  mode,
  products,
  onClose,
}: VehicleLeadModalProps) {
  const isFinancing = mode === "financing";

  const availableProducts = useMemo(
    () => products.filter((product) => product.is_available),
    [products],
  );

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [productId, setProductId] = useState("");
  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [occupation, setOccupation] = useState("");

  const [zipCode, setZipCode] = useState("");
  const [street, setStreet] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [addressComplement, setAddressComplement] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [isLookingUpPostalCode, setIsLookingUpPostalCode] =
    useState(false);
  const [postalCodeMessage, setPostalCodeMessage] =
    useState("");

  const [hasDownPayment, setHasDownPayment] =
    useState(false);
  const [downPaymentAmount, setDownPaymentAmount] =
    useState("");
  const [vehicleOfInterest, setVehicleOfInterest] =
    useState("");
  const [vehicleValue, setVehicleValue] = useState("");
  const [desiredInstallments, setDesiredInstallments] =
    useState("");

  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [version, setVersion] = useState("");
  const [manufactureYear, setManufactureYear] =
    useState("");
  const [modelYear, setModelYear] = useState("");
  const [fuel, setFuel] = useState("");
  const [transmission, setTransmission] = useState("");
  const [color, setColor] = useState("");
  const [mileage, setMileage] = useState("");
  const [plate, setPlate] = useState("");
  const [desiredValue, setDesiredValue] = useState("");
  const [observations, setObservations] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);

  const [isSubmitting, setIsSubmitting] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!isFinancing) {
      return;
    }

    const digits = onlyDigits(zipCode);

    if (digits.length !== 8) {
      setPostalCodeMessage("");
      return;
    }

    let cancelled = false;

    async function loadPostalCode() {
      setIsLookingUpPostalCode(true);
      setPostalCodeMessage("");

      try {
        const result = await lookupPostalCode(digits);

        if (cancelled) {
          return;
        }

        setStreet(result.address || "");
        setNeighborhood(result.district || "");
        setCity(result.city || "");
        setState(result.state || "");

        if (
          result.complement &&
          !addressComplement.trim()
        ) {
          setAddressComplement(result.complement);
        }

        setPostalCodeMessage(
          "Endereço localizado automaticamente.",
        );
      } catch {
        if (cancelled) {
          return;
        }

        setPostalCodeMessage(
          "Não foi possível localizar esse CEP. Preencha o endereço manualmente.",
        );
      } finally {
        if (!cancelled) {
          setIsLookingUpPostalCode(false);
        }
      }
    }

    void loadPostalCode();

    return () => {
      cancelled = true;
    };
  }, [zipCode, isFinancing]);

  useEffect(() => {
    if (!isFinancing) {
      return;
    }

    if (!productId) {
      setVehicleValue("");
      return;
    }

    const selectedProduct = availableProducts.find(
      (product) => product.id === Number(productId),
    );

    setVehicleValue(
      selectedProduct?.current_price ?? "",
    );
  }, [productId, availableProducts, isFinancing]);

  function handlePhotos(
    event: ChangeEvent<HTMLInputElement>,
  ) {
    const files = Array.from(event.target.files ?? []);

    if (files.length > 10) {
      setErrorMessage(
        "Você pode enviar no máximo 10 fotos.",
      );
      event.target.value = "";
      return;
    }

    const oversized = files.find(
      (file) => file.size > 5 * 1024 * 1024,
    );

    if (oversized) {
      setErrorMessage(
        "Cada foto deve possuir no máximo 5 MB.",
      );
      event.target.value = "";
      return;
    }

    setErrorMessage("");
    setPhotos(files);
  }

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    setErrorMessage("");
    setIsSubmitting(true);

    try {
      if (isFinancing) {
        const selectedProduct = availableProducts.find(
          (product) =>
            product.id === Number(productId),
        );

        await createPublicLead(companySlug, {
          name,
          phone,
          email,
          source: "store",
          lead_type: "financing",
          product_id: selectedProduct?.id,
          details: {
            cpf: onlyDigits(cpf),
            birth_date: birthDate,
            monthly_income: monthlyIncome,
            occupation,
            zip_code: onlyDigits(zipCode),
            street,
            address_number: addressNumber,
            address_complement: addressComplement,
            neighborhood,
            city,
            state: state.trim().toUpperCase(),
            has_down_payment: hasDownPayment,
            down_payment_amount: hasDownPayment
              ? downPaymentAmount
              : "",
            vehicle_of_interest:
              selectedProduct?.name ||
              vehicleOfInterest,
            vehicle_value: vehicleValue,
            desired_installments:
              Number(desiredInstallments),
          },
        });
      } else {
        if (photos.length === 0) {
          setErrorMessage(
            "Envie pelo menos uma foto do veículo.",
          );
          return;
        }

        const formData = new FormData();

        formData.append("name", name);
        formData.append("phone", phone);
        formData.append("email", email);
        formData.append("source", "store");
        formData.append(
          "lead_type",
          "vehicle_appraisal",
        );

        formData.append(
          "details",
          JSON.stringify({
            brand,
            model,
            version,
            manufacture_year: manufactureYear,
            model_year: modelYear,
            fuel,
            transmission,
            color,
            mileage,
            plate,
            desired_value: desiredValue,
            observations,
          }),
        );

        photos.forEach((photo) => {
          formData.append("photos", photo);
        });

        await createPublicLead(
          companySlug,
          formData,
        );
      }

      setIsSuccess(true);
    } catch (error) {
      if (error instanceof ApiError) {
        setErrorMessage(
          error.message ||
            "Não foi possível enviar a solicitação.",
        );
      } else {
        setErrorMessage(
          "Não foi possível enviar a solicitação.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isSuccess) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
          <CheckCircle2
            size={52}
            className="mx-auto text-emerald-500"
          />

          <h2 className="mt-5 text-2xl font-black text-slate-950">
            Solicitação enviada
          </h2>

          <p className="mt-3 text-sm leading-6 text-slate-500">
            A empresa recebeu seus dados e poderá
            entrar em contato com você.
          </p>

          <button
            type="button"
            onClick={onClose}
            className="mt-7 h-11 w-full rounded-xl bg-blue-600 text-sm font-bold text-white transition hover:bg-blue-700"
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm sm:p-5"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-5 sm:px-7">
          <div>
            <div className="flex items-center gap-2 text-blue-600">
              <Car size={20} />
              <span className="text-xs font-black uppercase tracking-[0.16em]">
                Veículos
              </span>
            </div>

            <h2 className="mt-2 text-2xl font-black text-slate-950">
              {isFinancing
                ? "Financiamento"
                : "Avalie seu carro"}
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              {isFinancing
                ? "Preencha seus dados para solicitar uma simulação."
                : "Envie os dados do seu veículo para solicitar uma avaliação."}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto"
        >
          <div className="space-y-7 p-5 sm:p-7">
            <section>
              <h3 className="text-sm font-black text-slate-900">
                Seus dados
              </h3>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label>
                  <span className={labelClass}>
                    Nome *
                  </span>
                  <input
                    required
                    value={name}
                    onChange={(event) =>
                      setName(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>

                <label>
                  <span className={labelClass}>
                    WhatsApp *
                  </span>
                  <input
                    required
                    inputMode="tel"
                    value={phone}
                    onChange={(event) =>
                      setPhone(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>

                <label className="sm:col-span-2">
                  <span className={labelClass}>
                    E-mail *
                  </span>
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(event) =>
                      setEmail(event.target.value)
                    }
                    className={inputClass}
                  />
                </label>
              </div>
            </section>

            {isFinancing ? (
              <section>
                <h3 className="text-sm font-black text-slate-900">
                  Dados para financiamento
                </h3>

                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label>
                    <span className={labelClass}>
                      CPF *
                    </span>
                    <input
                      required
                      inputMode="numeric"
                      value={cpf}
                      onChange={(event) =>
                        setCpf(
                          formatCpf(
                            event.target.value,
                          ),
                        )
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Data de nascimento *
                    </span>
                    <input
                      required
                      type="date"
                      min="1900-01-01"
                      max={new Date()
                        .toISOString()
                        .slice(0, 10)}
                      value={birthDate}
                      onChange={(event) =>
                        setBirthDate(
                          event.target.value,
                        )
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Renda mensal *
                    </span>
                    <input
                      required
                      inputMode="decimal"
                      value={monthlyIncome}
                      onChange={(event) =>
                        setMonthlyIncome(
                          event.target.value,
                        )
                      }
                      placeholder="Ex.: 6500"
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Ocupação *
                    </span>
                    <input
                      required
                      value={occupation}
                      onChange={(event) =>
                        setOccupation(
                          event.target.value,
                        )
                      }
                      className={inputClass}
                    />
                  </label>

                  <div className="sm:col-span-2 mt-2">
                    <h4 className="text-sm font-black text-slate-900">
                      Endereço residencial
                    </h4>
                  </div>

                  <label>
                    <span className={labelClass}>
                      CEP *
                    </span>
                    <input
                      required
                      inputMode="numeric"
                      maxLength={9}
                      value={zipCode}
                      onChange={(event) =>
                        setZipCode(
                          formatCep(event.target.value),
                        )
                      }
                      placeholder="00000-000"
                      className={inputClass}
                    />
                      {(isLookingUpPostalCode ||
                        postalCodeMessage) && (
                        <p
                          className={
                            "mt-1.5 text-xs " +
                            (isLookingUpPostalCode
                              ? "text-slate-500"
                              : postalCodeMessage.startsWith(
                                    "Endereço localizado",
                                  )
                                ? "text-emerald-600"
                                : "text-amber-600")
                          }
                        >
                          {isLookingUpPostalCode
                            ? "Buscando endereço..."
                            : postalCodeMessage}
                        </p>
                      )}
                  </label>

                  <label>
                    <span className={labelClass}>
                      Número *
                    </span>
                    <input
                      required
                      value={addressNumber}
                      onChange={(event) =>
                        setAddressNumber(event.target.value)
                      }
                      className={inputClass}
                    />
                  </label>

                  <label className="sm:col-span-2">
                    <span className={labelClass}>
                      Endereço *
                    </span>
                    <input
                      required
                      value={street}
                      onChange={(event) =>
                        setStreet(event.target.value)
                      }
                      placeholder="Rua, avenida, travessa..."
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Bairro *
                    </span>
                    <input
                      required
                      value={neighborhood}
                      onChange={(event) =>
                        setNeighborhood(event.target.value)
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Complemento
                    </span>
                    <input
                      value={addressComplement}
                      onChange={(event) =>
                        setAddressComplement(event.target.value)
                      }
                      placeholder="Apto, bloco..."
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Cidade *
                    </span>
                    <input
                      required
                      value={city}
                      onChange={(event) =>
                        setCity(event.target.value)
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      UF *
                    </span>
                    <input
                      required
                      maxLength={2}
                      value={state}
                      onChange={(event) =>
                        setState(
                          event.target.value
                            .replace(/[^a-zA-Z]/g, "")
                            .toUpperCase()
                            .slice(0, 2),
                        )
                      }
                      placeholder="SP"
                      className={inputClass}
                    />
                  </label>

                  {availableProducts.length > 0 && (
                    <label className="sm:col-span-2">
                      <span className={labelClass}>
                        Veículo da loja
                      </span>
                      <select
                        value={productId}
                        onChange={(event) =>
                          setProductId(
                            event.target.value,
                          )
                        }
                        className={inputClass}
                      >
                        <option value="">
                          Outro veículo / ainda não decidi
                        </option>

                        {availableProducts.map(
                          (product) => (
                            <option
                              key={product.id}
                              value={product.id}
                            >
                              {product.name}
                            </option>
                          ),
                        )}
                      </select>
                    </label>
                  )}

                  {!productId && (
                    <label className="sm:col-span-2">
                      <span className={labelClass}>
                        Veículo de interesse *
                      </span>
                      <input
                        required
                        value={vehicleOfInterest}
                        onChange={(event) =>
                          setVehicleOfInterest(
                            event.target.value,
                          )
                        }
                        placeholder="Ex.: SUV seminovo"
                        className={inputClass}
                      />
                    </label>
                  )}

                  <label>
                    <span className={labelClass}>
                      Valor aproximado do veículo *
                    </span>
                    <input
                      required
                      inputMode="decimal"
                      value={vehicleValue}
                      onChange={(event) =>
                        setVehicleValue(
                          event.target.value,
                        )
                      }
                      placeholder="Ex.: 85000"
                      className={inputClass}

                        readOnly={Boolean(productId)}/>
                  </label>

                  <label>
                    <span className={labelClass}>
                      Parcelas desejadas *
                    </span>
                    <select
                      required
                      value={desiredInstallments}
                      onChange={(event) =>
                        setDesiredInstallments(
                          event.target.value,
                        )
                      }
                      className={inputClass}
                    >
                      <option value="">
                        Selecione
                      </option>
                      {[12, 18, 24, 36, 48, 60, 72].map(
                        (installments) => (
                          <option
                            key={installments}
                            value={installments}
                          >
                            {installments}x
                          </option>
                        ),
                      )}
                    </select>
                  </label>

                  <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={hasDownPayment}
                      onChange={(event) =>
                        setHasDownPayment(
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-bold text-slate-700">
                      Tenho valor para entrada
                    </span>
                  </label>

                  {hasDownPayment && (
                    <label className="sm:col-span-2">
                      <span className={labelClass}>
                        Valor da entrada *
                      </span>
                      <input
                        required
                        inputMode="decimal"
                        value={downPaymentAmount}
                        onChange={(event) =>
                          setDownPaymentAmount(
                            event.target.value,
                          )
                        }
                        placeholder="Ex.: 15000"
                        className={inputClass}
                      />
                    </label>
                  )}
                </div>
              </section>
            ) : (
              <section>
                <h3 className="text-sm font-black text-slate-900">
                  Dados do veículo
                </h3>

                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label>
                    <span className={labelClass}>
                      Marca *
                    </span>
                    <input
                      required
                      value={brand}
                      onChange={(event) =>
                        setBrand(event.target.value)
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Modelo *
                    </span>
                    <input
                      required
                      value={model}
                      onChange={(event) =>
                        setModel(event.target.value)
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Versão *
                    </span>
                    <input
                      required
                      value={version}
                      onChange={(event) =>
                        setVersion(event.target.value)
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Cor *
                    </span>
                    <input
                      required
                      value={color}
                      onChange={(event) =>
                        setColor(event.target.value)
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Ano fabricação *
                    </span>
                    <input
                      required
                      inputMode="numeric"
                      maxLength={4}
                      value={manufactureYear}
                      onChange={(event) =>
                        setManufactureYear(
                          onlyDigits(
                            event.target.value,
                          ).slice(0, 4),
                        )
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Ano modelo *
                    </span>
                    <input
                      required
                      inputMode="numeric"
                      maxLength={4}
                      value={modelYear}
                      onChange={(event) =>
                        setModelYear(
                          onlyDigits(
                            event.target.value,
                          ).slice(0, 4),
                        )
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Combustível *
                    </span>
                    <select
                      required
                      value={fuel}
                      onChange={(event) =>
                        setFuel(event.target.value)
                      }
                      className={inputClass}
                    >
                      <option value="">
                        Selecione
                      </option>
                      <option value="Gasolina">
                        Gasolina
                      </option>
                      <option value="Etanol">
                        Etanol
                      </option>
                      <option value="Flex">Flex</option>
                      <option value="Diesel">
                        Diesel
                      </option>
                      <option value="Híbrido">
                        Híbrido
                      </option>
                      <option value="Elétrico">
                        Elétrico
                      </option>
                      <option value="Outro">
                        Outro
                      </option>
                    </select>
                  </label>

                  <label>
                    <span className={labelClass}>
                      Câmbio *
                    </span>
                    <select
                      required
                      value={transmission}
                      onChange={(event) =>
                        setTransmission(
                          event.target.value,
                        )
                      }
                      className={inputClass}
                    >
                      <option value="">
                        Selecione
                      </option>
                      <option value="Manual">
                        Manual
                      </option>
                      <option value="Automático">
                        Automático
                      </option>
                      <option value="CVT">CVT</option>
                      <option value="Automatizado">
                        Automatizado
                      </option>
                      <option value="Outro">
                        Outro
                      </option>
                    </select>
                  </label>

                  <label>
                    <span className={labelClass}>
                      Quilometragem *
                    </span>
                    <input
                      required
                      inputMode="numeric"
                      value={mileage}
                      onChange={(event) =>
                        setMileage(
                          onlyDigits(
                            event.target.value,
                          ),
                        )
                      }
                      className={inputClass}
                    />
                  </label>

                  <label>
                    <span className={labelClass}>
                      Placa *
                    </span>
                    <input
                      required
                      value={plate}
                      onChange={(event) =>
                        setPlate(
                          formatPlate(
                            event.target.value,
                          ),
                        )
                      }
                      className={inputClass}
                    />
                  </label>

                  <label className="sm:col-span-2">
                    <span className={labelClass}>
                      Valor desejado *
                    </span>
                    <input
                      required
                      inputMode="decimal"
                      value={desiredValue}
                      onChange={(event) =>
                        setDesiredValue(
                          event.target.value,
                        )
                      }
                      placeholder="Ex.: 95000"
                      className={inputClass}
                    />
                  </label>

                  <label className="sm:col-span-2">
                    <span className={labelClass}>
                      Observações
                    </span>
                    <textarea
                      value={observations}
                      onChange={(event) =>
                        setObservations(
                          event.target.value,
                        )
                      }
                      rows={4}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                    />
                  </label>

                  <label className="sm:col-span-2">
                    <span className={labelClass}>
                      Fotos do veículo *
                    </span>

                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5">
                      <div className="flex items-center gap-3">
                        <Camera
                          size={24}
                          className="text-blue-600"
                        />

                        <div>
                          <p className="text-sm font-bold text-slate-800">
                            Envie até 10 fotos
                          </p>
                          <p className="text-xs text-slate-500">
                            Máximo de 5 MB por foto.
                          </p>
                        </div>
                      </div>

                      <input
                        required
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotos}
                        className="mt-4 block w-full text-sm text-slate-600 file:mr-4 file:rounded-xl file:border-0 file:bg-blue-600 file:px-4 file:py-2.5 file:text-sm file:font-bold file:text-white"
                      />

                      {photos.length > 0 && (
                        <p className="mt-3 text-xs font-bold text-emerald-700">
                          {photos.length} foto
                          {photos.length === 1
                            ? ""
                            : "s"}{" "}
                          selecionada
                          {photos.length === 1
                            ? ""
                            : "s"}.
                        </p>
                      )}
                    </div>
                  </label>
                </div>
              </section>
            )}

            {errorMessage && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                {errorMessage}
              </div>
            )}
          </div>

          <div className="flex gap-3 border-t border-slate-100 bg-white px-5 py-4 sm:px-7">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="h-11 flex-1 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {isSubmitting && (
                <Loader2
                  size={17}
                  className="animate-spin"
                />
              )}

              {isSubmitting
                ? "Enviando..."
                : "Enviar solicitação"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
